import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { DevDrawer } from "@/components/DevDrawer";
import { MovieStudio } from "@/components/MovieStudio";
import { CampaignHub } from "@/components/CampaignHub";
import { StorybookReader } from "@/components/StorybookReader";
import { ChangeVoiceDialog } from "@/components/ChangeVoiceDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Clapperboard, Megaphone, BookOpen, ArrowLeft, Loader2, Globe, Palette,
} from "lucide-react";

export default function CommandCenter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("movie");
  const [notFound, setNotFound] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getProject(id);
      setProject(data);
      return data;
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      throw e;
    }
  }, [id]);

  useEffect(() => {
    let interval;
    refresh()
      .then((d) => {
        const first = ["movie", "campaign", "storybook"].find((k) => (d.outputs || []).includes(k)) || "movie";
        setTab(first);
        if (d.status === "processing") {
          interval = setInterval(async () => {
            try {
              const latest = await refresh();
              if (latest.status !== "processing") clearInterval(interval);
            } catch {
              clearInterval(interval);
            }
          }, 3000);
        }
      })
      .catch(() => {});
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-xl mx-auto px-5 py-28 text-center">
          <h1 className="font-display text-2xl font-semibold">Project not found</h1>
          <p className="text-[#94A3B8] mt-2">It may have been deleted.</p>
          <button onClick={() => navigate("/")} className="mt-5 text-[#3B82F6] hover:underline">Back to dashboard</button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[70vh]"><Loader2 className="h-8 w-8 animate-spin text-[#22C55E]" /></div>
      </div>
    );
  }

  const has = (k) => (project.outputs || []).includes(k);
  const processing = project.status === "processing";

  return (
    <div className="min-h-screen pb-24">
      <Navbar />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#F8FAFC] mb-5">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">{project.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[#94A3B8]">
              <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4" /> {project.output_language}</span>
              <span className="inline-flex items-center gap-1.5"><Palette className="h-4 w-4" /> {project.art_style}</span>
              {(has("movie") || has("storybook")) && <ChangeVoiceDialog project={project} refresh={refresh} />}
            </div>
          </div>
        </div>

        {processing && (
          <div data-testid="processing-banner" className="glass rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium"><Loader2 className="h-4 w-4 animate-spin text-[#22C55E]" /> {project.progress?.stage || "Processing…"}</div>
              <span className="text-sm text-[#94A3B8]">{project.progress?.percent || 0}%</span>
            </div>
            <Progress value={project.progress?.percent || 0} className="h-2 bg-white/10" />
            <p className="text-xs text-[#64748B] mt-3">Assets stream in live as the AI agents finish each stage. Feel free to explore the tabs.</p>
          </div>
        )}

        {project.status === "error" && (
          <div className="glass rounded-xl p-5 mb-6 border border-red-500/30 text-red-300 text-sm">Generation failed. Check the developer drawer logs for details.</div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#1E293B]/60 border border-white/10 p-1 rounded-full h-auto flex-wrap">
            {has("movie") && (
              <TabsTrigger data-testid="tab-movie" value="movie" className="rounded-full data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white px-4 py-2 text-sm">
                <Clapperboard className="h-4 w-4 mr-1.5" /> Movie & Short Film
              </TabsTrigger>
            )}
            {has("campaign") && (
              <TabsTrigger data-testid="tab-campaign" value="campaign" className="rounded-full data-[state=active]:bg-[#22C55E] data-[state=active]:text-[#0F1117] px-4 py-2 text-sm">
                <Megaphone className="h-4 w-4 mr-1.5" /> Campaign & Growth
              </TabsTrigger>
            )}
            {has("storybook") && (
              <TabsTrigger data-testid="tab-storybook" value="storybook" className="rounded-full data-[state=active]:bg-[#A855F7] data-[state=active]:text-white px-4 py-2 text-sm">
                <BookOpen className="h-4 w-4 mr-1.5" /> Interactive Storybook
              </TabsTrigger>
            )}
          </TabsList>

          <div className="mt-6">
            {has("movie") && <TabsContent value="movie"><MovieStudio project={project} refresh={refresh} /></TabsContent>}
            {has("campaign") && <TabsContent value="campaign"><CampaignHub project={project} /></TabsContent>}
            {has("storybook") && <TabsContent value="storybook"><StorybookReader project={project} /></TabsContent>}
          </div>
        </Tabs>
      </div>

      <DevDrawer transcript={project.transcript} logs={project.logs} rawLlm={project.raw_llm} />
    </div>
  );
}
